/**
 * Notification Service
 *
 * Answers "if I saved this form right now, who would it email?" for the
 * admin / super admin "Notify users" prompt.
 *
 * Recipient resolution is preference-aware and lives on the server: the
 * assignee's "assigned to me" toggle, per-company and per-user watch lists,
 * the approver pool, the inspection's customer contact. The client can't
 * derive that list, and a second copy of the rules would drift from the one
 * that actually sends — so it asks.
 *
 * Backend endpoint:
 *	 POST /api/v1/notification-recipients/preview	— resolve pending recipients
 *
 * The answer the admin gives travels back on the save itself, as
 * `notifyUsers` / `notifyEmails` on the work order or inspection payload.
 *
 * All endpoints require authentication (Sanctum Bearer token).
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Which record type the pending save is for. */
export type NotifiableKind = 'work_order' | 'inspection';

/**
 * The pending form values a preview is resolved against.
 *
 * Everything but `kind` is optional and means "unchanged" when omitted —
 * the server overlays what's given onto the stored row (or onto an empty
 * record, for a create) and works out which events would fire from there.
 */
export interface NotifyPreviewRequest {
	kind: NotifiableKind;
	/** Existing record being edited. Omit for a create. */
	id?: string | number | null;
	companyId?: string | number | null;
	assigneeId?: string | number | null;
	/** The rest of the crew on a new work order — they're emailed about it too. */
	additionalAssigneeIds?: Array<string | number>;
	/** The status the save would set — drives the approval / completion events. */
	status?: string | null;
	/** Inspection "Send to Client" toggle. */
	notifyClient?: boolean;
	contactEmail?: string | null;
	contactName?: string | null;
}

/** One event a pending save would fire, e.g. "Assignment". */
export interface NotifyPreviewEvent {
	/** Canonical event type, e.g. 'work_order.assigned'. */
	key: string;
	/** Short human label for the prompt. */
	label: string;
}

/**
 * A resolved recipient.
 *
 * Structurally a superset of the RecipientList molecule's `Recipient`, so it
 * renders there directly — declared here rather than imported so a service
 * doesn't depend on a component. The id is null for anyone who isn't a
 * platform user: the customer contact is a bare address on the record.
 */
export interface NotifyPreviewRecipient {
	id: number | null;
	name: string;
	email: string;
	/** Why they're on the list — "Assignee", "Notification watch list". */
	reason: string;
}

export interface NotifyPreview {
	events: NotifyPreviewEvent[];
	recipients: NotifyPreviewRecipient[];
}

/**
 * The admin's answer, spread onto the save payload. Absent keys mean "no
 * opinion" — the server notifies everyone it resolves, which is what every
 * non-admin save does.
 */
export interface NotifyDecision {
	/** False = email nobody for this save. */
	notifyUsers?: boolean;
	/** Allow-list of addresses that stayed checked in the prompt. */
	notifyEmails?: string[];
}

/* ============================================
 * SERVICE
 * ============================================ */

export const notificationService = {
	/**
	 * Resolve who a pending work order / inspection save would email.
	 *
	 * Admin-tier only — the endpoint 403s for everyone else, since the answer
	 * enumerates people and addresses.
	 *
	 * Response keys are already single-word, so no camelization pass is
	 * needed (the client snakifies outgoing bodies but leaves incoming keys
	 * alone).
	 */
	previewRecipients: async (request: NotifyPreviewRequest): Promise<NotifyPreview> => {
		const response = await api.post<{ data: NotifyPreview }>('/notification-recipients/preview', request);
		const payload = (response.data as any)?.data ?? response.data;

		return {
			events: payload?.events ?? [],
			recipients: payload?.recipients ?? [],
		};
	},
};

export default notificationService;
